// 档2 — 豆包播客语音合成 playground。两种模式：
//   · 主题联网生成 (action=4)：给一个话题 prompt_text → 模型联网搜集 → 生成双人播客（**推荐**）。
//   · 对话稿配音 (action=3)：给现成对话稿 nlp_texts → 直接配成双人播客。
//
// 生命周期：生成 → StartConnection → ConnectionStarted(50) → StartSession(100) →
//   SessionStarted(150) → **FinishSession(102)**（关键：不发则不生成/不收尾，官方 SDK 同）→ 循环
//   RoundStart(360,{speaker,text}) / RoundResponse(361, 音频 Int16@24k) / RoundEnd(362) / Usage(154)
//   → PodcastEnd(363) / SessionFinished(152) → FinishConnection(2)。音频 Int16 PCM@24k（本地实测）。
//
// ⚠️ 主题联网生成较慢（约 1–3 分钟出整期）且成本较高（联网 input_text token 是大头，一期可达 $1+）。

import React from 'react';
import { XPrim, XIcons } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import WsRealtimeOrb from './ws-realtime-orb.jsx';
import { DoubaoRealtimeClient, DoubaoServerEvent as SE } from './ws-doubao-client.js';

const RATE_OUT = 24000; // 下行 PCM Int16 mono（本地探针实测：Int16@24k）

const VOICES = [
  { id: 'zh_male_dayixiansheng_v2_saturn_bigtts', label: '大异先生（男）' },
  { id: 'zh_female_mizaitongxue_v2_saturn_bigtts', label: '米仔同学（女）' },
  { id: 'zh_male_liufei_v2_saturn_bigtts', label: '刘飞（男）' },
  { id: 'zh_male_xiaolei_v2_saturn_bigtts', label: '小雷（男）' },
];
const DEFAULT_TOPIC = '用轻松的方式聊聊人工智能 Agent 的发展趋势';
const DEFAULT_SCRIPT = [
  '今天我们来聊聊火山引擎的语音大模型。',
  '好呀，它都能做什么呢？',
  '实时语音对话、语音合成、同声传译，还有我们正在用的播客生成。',
  '听起来很全面，那这期就从播客开始吧。',
].join('\n');

const PHASE_LABEL = { idle: '未连接', connecting: '连接中…', generating: '生成中…', done: '生成完成' };

// 对话稿按行拆成 nlp_texts：每行一轮，甲/乙交替；行首可用「甲:」「乙:」「A:」「B:」显式指定。
function parseScript(text, vA, vB) {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  return lines.map((line, i) => {
    let speaker = i % 2 === 0 ? vA : vB;
    let t = line;
    const m = line.match(/^(甲|乙|A|B|a|b)\s*[:：]\s*(.*)$/);
    if (m) { speaker = /甲|A|a/.test(m[1]) ? vA : vB; t = m[2]; }
    return { speaker, text: t.slice(0, 300) };
  });
}

export default function WsPlaygroundDoubaoPodcast({ api, endpoint }) {
  const wsConfig = (endpoint && endpoint._raw && endpoint._raw.wsConfig) || {};
  const wsBilling = (endpoint && endpoint._raw && endpoint._raw.wsBilling) || {};
  const clientPath = wsConfig.clientPath || '/v1/podcast';
  const inTextUsd = Number(wsBilling.inputTextTokenUsd) || 0;
  const outAudioUsd = Number(wsBilling.outputAudioTokenUsd) || 0;

  const [mode, setMode] = React.useState('topic'); // topic(action=4) | script(action=3)
  const [status, setStatus] = React.useState('idle'); // idle|connecting|generating|done|error
  const [topic, setTopic] = React.useState(DEFAULT_TOPIC);
  const [personaStyle, setPersonaStyle] = React.useState(''); // 主题模式的人设/风格提示（揉进 prompt_text）
  const [script, setScript] = React.useState(DEFAULT_SCRIPT);
  const [voiceA, setVoiceA] = React.useState(VOICES[0].id);
  const [voiceB, setVoiceB] = React.useState(VOICES[1].id);
  const [rounds, setRounds] = React.useState([]); // [{ speaker, text }]
  const [usage, setUsage] = React.useState({ inText: 0, outAudio: 0 });
  const [errorMsg, setErrorMsg] = React.useState('');
  const [phase, setPhase] = React.useState('idle');

  const clientRef = React.useRef(new DoubaoRealtimeClient());
  const wsRef = React.useRef(null);
  const statusRef = React.useRef('idle');
  const sidRef = React.useRef(null);
  const chainRef = React.useRef(Promise.resolve());
  const payloadRef = React.useRef(null);
  const playCtxRef = React.useRef(null);
  const playAtRef = React.useRef(0);
  const outAnalyserRef = React.useRef(null);
  const micAnalyserRef = React.useRef(null); // 恒 null（无麦克风）

  const setStatusBoth = (s) => { statusRef.current = s; setStatus(s); };

  const closeWs = React.useCallback(() => {
    if (wsRef.current) { try { wsRef.current.close(1000, 'client stop'); } catch {} wsRef.current = null; }
  }, []);
  React.useEffect(() => () => {
    closeWs();
    if (playCtxRef.current) { try { playCtxRef.current.close(); } catch {} playCtxRef.current = null; }
  }, [closeWs]);

  // 播放 Int16 LE @24k（无缝排队；AudioContext 跨会话复用）
  const playAudio = React.useCallback((bytes) => {
    try {
      if (!bytes || bytes.byteLength < 2) return;
      if (!playCtxRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        playCtxRef.current = new AudioCtx();
        playAtRef.current = playCtxRef.current.currentTime;
        const an = playCtxRef.current.createAnalyser();
        an.fftSize = 1024; an.connect(playCtxRef.current.destination);
        outAnalyserRef.current = an;
      }
      const ctx = playCtxRef.current;
      if (ctx.state === 'suspended') { try { ctx.resume(); } catch {} }
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      const n = Math.floor(bytes.byteLength / 2);
      if (n === 0) return;
      const buf = ctx.createBuffer(1, n, RATE_OUT);
      const ch = buf.getChannelData(0);
      for (let i = 0; i < n; i++) ch[i] = view.getInt16(i * 2, true) / 0x8000;
      const src = ctx.createBufferSource();
      src.buffer = buf; src.connect(outAnalyserRef.current || ctx.destination);
      const at = Math.max(playAtRef.current, ctx.currentTime);
      src.start(at); playAtRef.current = at + buf.duration;
    } catch { /* 忽略坏帧 */ }
  }, []);

  const finishAndClose = React.useCallback(() => {
    try { wsRef.current?.send(clientRef.current.finishConnection()); } catch {}
    closeWs();
    if (statusRef.current !== 'error') setStatusBoth('done');
    setPhase('done');
  }, [closeWs]);

  const handleFrame = React.useCallback((f) => {
    if (!f) return;
    if (f.audio) { playAudio(f.audio); return; } // 361 RoundResponse 音频
    if (f.msgType === 15) { setErrorMsg(`上游错误 code=${f.code || ''} ${(f.payload && f.payload.error) || ''}`); setStatusBoth('error'); return; }
    switch (f.event) {
      case SE.ConnectionStarted: // 50 → StartSession（本次生成配置）
        sidRef.current = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `s-${Date.now()}`;
        wsRef.current?.send(clientRef.current.startSession(sidRef.current, { ...payloadRef.current, input_id: sidRef.current }));
        break;
      case SE.SessionStarted: // 150 → 必须发 FinishSession 才触发生成 + 干净收尾
        setPhase('generating');
        try { wsRef.current?.send(clientRef.current.finishSession(sidRef.current)); } catch {}
        break;
      case 360: // RoundStart：{ round_id, speaker, text }
        if (f.payload && typeof f.payload.text === 'string') {
          setRounds((r) => [...r, { speaker: f.payload.speaker || '', text: f.payload.text }]);
        }
        break;
      case SE.Usage: { // 154：{ usage: { input_text_tokens, output_audio_tokens } }
        const u = (f.payload && (f.payload.usage || f.payload)) || {};
        const it = Number(u.input_text_tokens) || 0, oa = Number(u.output_audio_tokens) || 0;
        if (it || oa) setUsage((p) => ({ inText: p.inText + it, outAudio: p.outAudio + oa }));
        break;
      }
      case 363: // PodcastEnd
      case SE.SessionFinished: finishAndClose(); break; // 152
      case SE.SessionFailed:
      case SE.ConnectionFailed:
        setErrorMsg((f.payload && (f.payload.error || JSON.stringify(f.payload))) || `事件 ${f.event} 失败`);
        setStatusBoth('error');
        break;
      default: break;
    }
  }, [playAudio, finishAndClose]);

  const generate = async () => {
    if (statusRef.current === 'connecting' || statusRef.current === 'generating') return;
    // 组装 StartSession payload（按模式）
    const common = {
      input_info: { input_url: '', return_audio_url: false, only_nlp_text: false },
      speaker_info: { random_order: false, speakers: [voiceA, voiceB] },
      audio_config: { format: 'pcm', sample_rate: RATE_OUT, speech_rate: 0 },
      use_head_music: false, use_tail_music: false,
    };
    if (mode === 'topic') {
      const t = topic.trim();
      if (!t) { setErrorMsg('请输入一个话题'); return; }
      // 播客协议无独立人设字段（StartSession 只有 prompt_text）——把风格/人设自然语言揉进 prompt_text，
      // 由生成模型据此塑造主持人对话的语气与视角。
      const s = personaStyle.trim();
      const promptText = s ? `${t}\n\n风格与人设要求：${s}` : t;
      payloadRef.current = { ...common, action: 4, prompt_text: promptText };
    } else {
      const nlp = parseScript(script, voiceA, voiceB);
      if (!nlp.length) { setErrorMsg('请先输入对话脚本（每行一轮）'); return; }
      payloadRef.current = { ...common, action: 3, nlp_texts: nlp };
    }
    setErrorMsg(''); setRounds([]); setUsage({ inText: 0, outAudio: 0 }); sidRef.current = null;
    setStatusBoth('connecting'); setPhase('connecting');
    let token;
    try {
      const minted = await XApi.keyService.getWsToken(endpoint._raw?.id || endpoint.id);
      token = minted && minted.token;
      if (!token) throw new Error('mint returned no token');
    } catch (e) { setStatusBoth('error'); setErrorMsg('ws-token mint 失败: ' + (e && e.message ? e.message : e)); return; }

    const url = XApi.helpers.buildWsGatewayUrl(api.host, clientPath, { endpoint: endpoint._raw?.id || endpoint.id });
    let ws;
    try { ws = new WebSocket(url, XApi.helpers.buildWsCredentialProtocols(token)); } catch (e) { setStatusBoth('error'); setErrorMsg('WebSocket 构造失败: ' + (e && e.message ? e.message : e)); return; }
    ws.binaryType = 'arraybuffer'; wsRef.current = ws;
    ws.onopen = () => ws.send(clientRef.current.startConnection());
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') return;
      chainRef.current = chainRef.current.then(async () => { handleFrame(await clientRef.current.parseServerFrame(ev.data)); }).catch(() => {});
    };
    ws.onerror = () => setErrorMsg((m) => m || 'WebSocket error');
    ws.onclose = () => { wsRef.current = null; if (statusRef.current === 'connecting' || statusRef.current === 'generating') { if (statusRef.current !== 'error') setStatusBoth('done'); setPhase('done'); } };
  };

  const stop = () => { closeWs(); setStatusBoth('idle'); setPhase('idle'); };

  const busy = status === 'connecting' || status === 'generating';
  const estCost = usage.inText * inTextUsd + usage.outAudio * outAudioUsd;
  const dot = status === 'generating' ? 'var(--ok, #35c98a)' : status === 'connecting' ? 'var(--c-amber, #f0b23a)' : status === 'error' ? 'var(--err)' : status === 'done' ? 'var(--ok,#35c98a)' : 'var(--fg-4)';

  const ModeBtn = ({ id, children }) => (
    <button className="btn" onClick={() => !busy && setMode(id)} disabled={busy}
      style={{ height: 30, fontSize: 12.5, padding: '0 12px', borderColor: mode === id ? 'var(--c-blue,#5a86dc)' : 'var(--line)', color: mode === id ? 'var(--c-blue,#5a86dc)' : 'var(--fg-3)', background: mode === id ? 'rgba(90,134,220,0.08)' : 'transparent' }}>
      {children}
    </button>
  );

  return (
    <div className="col gap-3" style={{ minWidth: 0 }}>
      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">播客生成 · 豆包播客语音合成</div>
          <div className="row gap-2" style={{ alignItems: 'center', fontSize: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
            <span style={{ color: 'var(--fg-3)' }}>{status}</span>
          </div>
        </div>
        <div style={{ padding: 14 }} className="col gap-3">
          <div className="row gap-2" style={{ alignItems: 'center' }}>
            <ModeBtn id="topic">🎙️ 主题联网生成</ModeBtn>
            <ModeBtn id="script">📝 对话稿配音</ModeBtn>
          </div>
          <div className="row gap-3" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            {!busy ? (
              <button className="btn btn-primary" onClick={generate} style={{ height: 38, minWidth: 130 }}><XIcons.IconPlay size={14} /> 生成播客</button>
            ) : (
              <button className="btn" onClick={stop} style={{ height: 38, minWidth: 130, borderColor: 'var(--err)', color: 'var(--err)' }}>
                {status === 'connecting' ? <><div className="spinner" style={{ width: 14, height: 14 }} /> 连接中…</> : <><XIcons.IconPause size={14} /> 停止</>}
              </button>
            )}
            <label className="row gap-2" style={{ alignItems: 'center', fontSize: 12.5, color: 'var(--fg-3)' }}>
              <span>甲</span>
              <select className="input" value={voiceA} disabled={busy} onChange={(e) => setVoiceA(e.target.value)} style={{ height: 32, maxWidth: 160 }}>
                {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
            </label>
            <label className="row gap-2" style={{ alignItems: 'center', fontSize: 12.5, color: 'var(--fg-3)' }}>
              <span>乙</span>
              <select className="input" value={voiceB} disabled={busy} onChange={(e) => setVoiceB(e.target.value)} style={{ height: 32, maxWidth: 160 }}>
                {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
            </label>
          </div>
          {mode === 'topic' ? (
            <>
              <textarea className="input" rows={2} style={{ resize: 'vertical', width: '100%' }}
                placeholder="输入一个话题，AI 联网搜集资料并生成整期双人播客…"
                value={topic} disabled={busy} onChange={(e) => setTopic(e.target.value)} />
              <label className="col gap-1">
                <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>风格 / 人设（可选）</span>
                <input className="input" value={personaStyle} disabled={busy} onChange={(e) => setPersonaStyle(e.target.value)}
                  placeholder="例如：轻松幽默、像两个老友闲聊；或专业严谨、面向开发者深度解读。" />
                <span style={{ fontSize: 11, color: 'var(--fg-4)' }}>播客协议无独立人设字段，此处会作为自然语言要求并入话题提示，由模型据此塑造主持人语气。</span>
              </label>
              <div style={{ fontSize: 11.5, color: 'var(--c-amber,#f0b23a)' }}>⚠️ 主题联网生成约需 1–3 分钟出整期，成本较高（联网 input_text token 是大头，一期可达 $1+）。</div>
            </>
          ) : (
            <>
              <textarea className="input" rows={5} style={{ resize: 'vertical', width: '100%' }}
                placeholder="每行一轮对话，甲/乙交替；也可用「甲:」「乙:」显式指定说话人。"
                value={script} disabled={busy} onChange={(e) => setScript(e.target.value)} />
              <div style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>对话稿直配音：每行一轮（单轮 ≤300 字），甲/乙交替朗读。计费按 token（输出音频为主）。</div>
            </>
          )}
        </div>
      </div>

      {errorMsg && (
        <div className="row gap-2" style={{ padding: '10px 12px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 8, fontSize: 12.5, color: 'var(--err)' }}>
          <XIcons.IconInfo size={13} /> <span style={{ overflowWrap: 'anywhere' }}>{errorMsg}</span>
        </div>
      )}

      <div className="card">
        <div className="row between" style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)' }}>
          <div className="micro">输出{mode === 'topic' ? ' · 联网生成' : ''}</div>
          {(usage.inText > 0 || usage.outAudio > 0) && (
            <div style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>
              输入文本 {usage.inText} tok · 输出音频 {usage.outAudio} tok{estCost > 0 ? ` · ~$${estCost.toFixed(4)}` : ''}
            </div>
          )}
        </div>
        <div className="col" style={{ alignItems: 'center', justifyContent: 'center', gap: 12, padding: '22px 16px 8px', minHeight: 150,
          background: 'radial-gradient(120% 80% at 50% 32%, rgba(80,120,220,0.06), transparent)' }}>
          <WsRealtimeOrb micAnalyserRef={micAnalyserRef} outAnalyserRef={outAnalyserRef} phase={busy || status === 'done' ? phase : 'idle'} size={132} />
          <div className="micro" style={{ color: 'var(--fg-3)' }}>{PHASE_LABEL[status] || status}{status === 'generating' && mode === 'topic' ? '（联网中，请耐心等待）' : ''}</div>
        </div>
        <div className="col gap-2" style={{ padding: 14, borderTop: '1px solid var(--line)', maxHeight: 320, overflowY: 'auto' }}>
          {rounds.length === 0
            ? <div style={{ fontSize: 12.5, color: 'var(--fg-4)', textAlign: 'center', padding: '8px 0' }}>生成的每轮对话会带说话人显示在这里，音频自动播放</div>
            : rounds.map((r, i) => {
                const isA = r.speaker === voiceA;
                return (
                  <div key={i} className="row gap-2" style={{ alignItems: 'flex-start', flexDirection: isA ? 'row' : 'row-reverse' }}>
                    <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: isA ? 'var(--c-blue, #5a86dc)' : 'var(--c-amber, #f0b23a)', minWidth: 22 }}>{isA ? '甲' : '乙'}</span>
                    <div style={{ fontSize: 13.5, color: 'var(--fg-1)', background: 'var(--bg-2, rgba(127,127,127,0.06))', padding: '7px 11px', borderRadius: 10, maxWidth: '80%', overflowWrap: 'anywhere' }}>{r.text}</div>
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
}
